import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Scenario, ScenarioId } from '../types/census';
import { scenarios } from '../data/populations';
const STORAGE_KEY = 'agentpopulation:scenario';
interface ScenarioContextValue {
  scenarioId: ScenarioId;
  scenario: Scenario;
  setScenarioId: (id: ScenarioId) => void;
}
const ScenarioContext = createContext<ScenarioContextValue | null>(null);
function isScenarioId(v: unknown): v is ScenarioId {
  return v === 'verifiable' || v === 'device' || v === 'ambient';
}
function readStored(): ScenarioId | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isScenarioId(v) ? v : null;
  } catch {
    return null;
  }
}
export function ScenarioProvider({
  initial,
  children
}: {
  initial: ScenarioId;
  children: React.ReactNode;
}) {
  const [scenarioId, setScenarioId] = useState<ScenarioId>(() => readStored() ?? initial);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, scenarioId);
    } catch {
      /* storage unavailable — context still shares state in-session */
    }
  }, [scenarioId]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isScenarioId(e.newValue)) setScenarioId(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const value = useMemo(() => ({
    scenarioId,
    setScenarioId,
    scenario: scenarios.find(s => s.id === scenarioId) ?? scenarios[1]
  }), [scenarioId]);
  return <ScenarioContext.Provider value={value} data-id="element-866" data-mp="3jgt4oxexbpkv">{children}</ScenarioContext.Provider>;
}
export function useScenario(): ScenarioContextValue {
  const ctx = useContext(ScenarioContext);
  if (!ctx) throw new Error('useScenario must be used within ScenarioProvider');
  return ctx;
}